import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  DeviceSyncField,
  BusinessDataV2Type,
  BusinessUserV2Client,
  RecurringPlanStatusEnum,
} from '@am3nd-org/pointcraft-types';

import { syncDeviceSyncV4Fields } from 'src/utils/Services/device-sync-v4.service';
import {
  updateBusinessProfile,
  type BusinessProfilePatchRequest,
} from 'src/utils/Services/business-profile.service';

import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { getSessionGeneration, isSessionGenerationCurrent } from 'src/auth/session-lifecycle';

import type { BusinessProfile } from '../types';
import { type Employee } from '../schemas/employee';
import { storeEvents, STORE_EVENTS } from '../utils';
import { employeeApi, subscriptionPlanApi } from '../utils/api';
import type { CreateRecurringPlanDTO } from '../schemas/recurringPlan';
import { mapUsersToEmployees, mapBusinessToBusinessProfile } from '../utils/mappers';

/* ====================== SETTINGS STORE STATE ====================== */
interface SettingsStoreState {
  // STATES
  employees: Employee[];
  businessProfile: BusinessProfile | null;
  isLoading: boolean;
  error: string | null;
  employeeLoading: boolean;
  businessProfileLoading: boolean;
  plansLoading: boolean;

  // RECURRING PLANS OPERATIONS (Async API)
  fetchPlansAsync: () => Promise<void>;
  createPlanAsync: (plan: CreateRecurringPlanDTO) => Promise<{ success: boolean; error?: string }>;
  updatePlanStatusAsync: (
    id: string,
    status: RecurringPlanStatusEnum
  ) => Promise<{ success: boolean; error?: string }>;

  // EMPLOYEES OPERATIONS (LOCAL)
  getEmployees: () => Employee[];
  getEmployeeById: (id: string) => Employee | undefined;
  createEmployee: (employee: Omit<Employee, 'id'>) => Employee;
  updateEmployee: (id: string, updates: Partial<Employee>) => void;
  deleteEmployee: (id: string) => void;

  // EMPLOYEES OPERATIONS (ASYNC API)
  createEmployeeAsync: (
    employee: Omit<Employee, 'id' | 'joinedDate'>
  ) => Promise<{ success: boolean; error?: string }>;
  updateEmployeeAsync: (
    id: string,
    updates: Partial<Employee>
  ) => Promise<{ success: boolean; error?: string }>;
  deleteEmployeeAsync: (id: string) => Promise<{ success: boolean; error?: string }>;

  // BUSINESS PROFILE OPERATIONS (LOCAL)
  getBusinessProfile: () => BusinessProfile | null;
  updateBusinessProfile: (updates: Partial<BusinessProfile>) => void;

  // BUSINESS PROFILE OPERATIONS (ASYNC API)
  updateBusinessProfileAsync: (
    updates: BusinessProfilePatchRequest
  ) => Promise<{ success: boolean; error?: string }>;

  // SYNC OPERATIONS (from useDeviceSyncStore)
  syncEmployeesFromUserStore: (users: BusinessUserV2Client[]) => void;
  syncBusinessProfileFromUserStore: (business: BusinessDataV2Type) => void;

  // UTILITIES
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/* ====================== SETTINGS STORE ====================== */
export const useSettingStore = create<SettingsStoreState>()(
  devtools(
    (set, get) => ({
      // INITIAL STATE
      employees: [] as Employee[], // Synced from useDeviceSyncStore
      businessProfile: null, // Synced from useDeviceSyncStore
      isLoading: false,
      error: null,
      employeeLoading: false,
      businessProfileLoading: false,
      plansLoading: false,

      // RECURRING PLANS OPERATIONS (Async API)
      fetchPlansAsync: async () => {
        const generation = getSessionGeneration();
        set({ plansLoading: true, error: null });
        try {
          const response = await subscriptionPlanApi.getAll();
          if (!isSessionGenerationCurrent(generation)) return;
          if (response.success && response.data) {
            await syncDeviceSyncV4Fields([DeviceSyncField.RECURRING_PLANS]);
            if (!isSessionGenerationCurrent(generation)) return;
            set({ plansLoading: false });
          } else {
            const errorMessage = response.error?.details?.message || 'Failed to fetch plans';
            set({ plansLoading: false, error: errorMessage });
          }
        } catch (error) {
          if (!isSessionGenerationCurrent(generation)) return;
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch plans';
          set({ plansLoading: false, error: errorMessage });
        }
      },

      createPlanAsync: async (planData) => {
        const generation = getSessionGeneration();
        set({ plansLoading: true, error: null });
        try {
          const response = await subscriptionPlanApi.create(planData);
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          if (response.success) {
            // Refetch plans to get the created plan with server-generated fields
            await get().fetchPlansAsync();
            if (!isSessionGenerationCurrent(generation))
              return { success: false, error: 'Your sign-in changed. Please try again.' };
            storeEvents.emit(STORE_EVENTS.PLAN_CREATED, planData);
            return { success: true };
          }
          const errorMessage = response.error?.details?.message || 'Failed to create plan';
          set({ plansLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        } catch (error) {
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          const errorMessage = error instanceof Error ? error.message : 'Failed to create plan';
          set({ plansLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      updatePlanStatusAsync: async (id, status) => {
        const generation = getSessionGeneration();
        set({ plansLoading: true, error: null });
        try {
          const response = await subscriptionPlanApi.updateStatus(id, status);
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          if (response.success) {
            await syncDeviceSyncV4Fields([DeviceSyncField.RECURRING_PLANS]);
            if (!isSessionGenerationCurrent(generation))
              return { success: false, error: 'Your sign-in changed. Please try again.' };
            set({ plansLoading: false });
            return { success: true };
          }
          const errorMessage = response.error?.details?.message || 'Failed to update plan';
          set({ plansLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        } catch (error) {
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          const errorMessage = error instanceof Error ? error.message : 'Failed to update plan';
          set({ plansLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      // EMPLOYEES OPERATIONS
      getEmployees: () => get().employees,

      getEmployeeById: (id) => get().employees.find((employee) => employee.id === id),

      createEmployee: (employee) => {
        const newEmployee: Employee = {
          ...employee,
          id: `emp_${crypto.randomUUID() as string}`,
          joinedDate: new Date(),
        };

        set((state) => ({
          employees: [...state.employees, newEmployee],
        }));

        // EMIT EVENT FOR NEW EMPLOYEE
        storeEvents.emit(STORE_EVENTS.EMPLOYEE_CREATED, newEmployee);

        return newEmployee;
      },

      updateEmployee: (id, updates) => {
        set((state) => ({
          employees: state.employees.map((employee) =>
            employee.id === id ? { ...employee, ...updates } : employee
          ),
        }));

        // EMIT EVENT FOR UPDATED EMPLOYEE
        const updatedEmployee = get().getEmployeeById(id);
        if (updatedEmployee) {
          storeEvents.emit(STORE_EVENTS.EMPLOYEE_UPDATED, updatedEmployee);
        }
      },

      deleteEmployee: (id: string) => {
        const deletedEmployee = get().getEmployeeById(id);

        set((state) => ({
          employees: state.employees.filter((employee) => employee.id !== id),
        }));

        // EMIT EVENT FOR DELETED EMPLOYEE
        if (deletedEmployee) {
          storeEvents.emit(STORE_EVENTS.EMPLOYEE_DELETED, deletedEmployee);
        }
      },

      // EMPLOYEES ASYNC API OPERATIONS
      createEmployeeAsync: async (employeeData) => {
        const generation = getSessionGeneration();
        set({ employeeLoading: true, error: null });
        try {
          const response = await employeeApi.create(employeeData);
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          if (response.success && response.data?.userId) {
            const employee: Employee = {
              ...employeeData,
              id: response.data.userId,
              joinedDate: new Date(),
            };
            // The creation response contains credentials and an id, not an employee profile.
            set((state) => ({
              employees: [...state.employees, employee],
              employeeLoading: false,
            }));
            storeEvents.emit(STORE_EVENTS.EMPLOYEE_CREATED, employee);
            return { success: true };
          }
          const errorMessage = response.error?.details?.message || 'Failed to create employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        } catch (error) {
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          const errorMessage = error instanceof Error ? error.message : 'Failed to create employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      updateEmployeeAsync: async (id, updates) => {
        const generation = getSessionGeneration();
        set({ employeeLoading: true, error: null });
        try {
          const response = await employeeApi.update(id, updates);
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          if (response.success && response.data) {
            // Update local store after successful API call
            set((state) => ({
              employees: state.employees.map((emp) =>
                emp.id === id ? { ...emp, ...response.data } : emp
              ),
              employeeLoading: false,
            }));
            storeEvents.emit(STORE_EVENTS.EMPLOYEE_UPDATED, response.data);
            return { success: true };
          }
          const errorMessage = response.error?.details?.message || 'Failed to update employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        } catch (error) {
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          const errorMessage = error instanceof Error ? error.message : 'Failed to update employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      deleteEmployeeAsync: async (id) => {
        const generation = getSessionGeneration();
        set({ employeeLoading: true, error: null });
        try {
          const deletedEmployee = get().getEmployeeById(id);
          const response = await employeeApi.delete(id);
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          if (response.success) {
            // Remove from local store after successful API call
            set((state) => ({
              employees: state.employees.filter((emp) => emp.id !== id),
              employeeLoading: false,
            }));
            if (deletedEmployee) {
              storeEvents.emit(STORE_EVENTS.EMPLOYEE_DELETED, deletedEmployee);
            }
            return { success: true };
          }
          const errorMessage = response.error?.details?.message || 'Failed to delete employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        } catch (error) {
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      // BUSINESS PROFILE OPERATIONS
      getBusinessProfile: () => get().businessProfile,

      updateBusinessProfile: (profile) => {
        set((state) => ({
          businessProfile: { ...(state.businessProfile || {}), ...profile } as BusinessProfile,
        }));
      },

      // BUSINESS PROFILE ASYNC API OPERATIONS
      updateBusinessProfileAsync: async (updates) => {
        const generation = getSessionGeneration();
        set({ businessProfileLoading: true, error: null });
        try {
          const profile = await updateBusinessProfile(updates);
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          set((state) => ({
            businessProfile: {
              companyName:
                state.businessProfile?.companyName ??
                useDeviceSyncStore.getState().businessData?.name ??
                '',
              email: profile.email,
              phone: profile.phone,
              address: profile.address,
            },
            businessProfileLoading: false,
          }));
          return { success: true };
        } catch (error) {
          if (!isSessionGenerationCurrent(generation))
            return { success: false, error: 'Your sign-in changed. Please try again.' };
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to update business profile';
          set({ businessProfileLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      // SYNC OPERATIONS (from useDeviceSyncStore)
      syncEmployeesFromUserStore: (users: BusinessUserV2Client[]) => {
        const mappedEmployees = mapUsersToEmployees(users);
        set({ employees: mappedEmployees });
      },

      syncBusinessProfileFromUserStore: (business: BusinessDataV2Type) => {
        const mappedProfile = mapBusinessToBusinessProfile(business);
        set({ businessProfile: mappedProfile });
      },

      // UTILITIES
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: string | null) => set({ error }),
    }),
    { name: 'settings-store' }
  )
);
